/**
 * SkuVariantPicker.jsx — typeahead picker that searches SKUs by code/name/
 * barcode, then lets the user choose a specific variant when the SKU has
 * more than one. Replaces the long native <select> we previously had on
 * the Create Receipt + Supplier Templates editors when catalogs grew past
 * a few hundred variants.
 *
 * Props:
 *   - skus              array of SKUs ({id, sku_code, name})
 *   - variants          array of variants ({id, sku_id, barcode, size, color})
 *   - value             currently selected id (variant_id by default; sku_id when mode='sku')
 *   - onChange          (id) => void — emits variant_id (default) or sku_id (when mode='sku')
 *   - mode              'variant' (default) or 'sku' — when 'sku', clicking a SKU
 *                       row emits the sku_id directly without the variant chooser
 *   - testIdPrefix      base for data-testid on the input + result rows
 *   - placeholder       (optional) input placeholder
 *   - autoFocus         (optional) focus the search field on mount
 *
 * Behaviour:
 *   - Empty input + no value → renders just the input.
 *   - Empty input + value selected → renders the chosen variant chip with a
 *     "Change" button that re-opens the search.
 *   - Input typed → shows up to 12 matching SKUs (case-insensitive). Each
 *     row shows: SKU code (mono), name, and a variant-count badge.
 *   - mode='variant' (default): click a SKU with exactly 1 variant → selects
 *     it immediately. Click a SKU with 2+ variants → expands a sub-list of
 *     variants under that row; click one to select.
 *   - mode='sku': click any SKU → emits its sku_id; variant lookup is skipped.
 *
 * Performance:
 *   - Indexes (variantsBySku, skusById, label) are memoized — re-rendering
 *     1000 rows is cheap even on mid-tier Android.
 *   - Caps results at 12 to keep the dropdown short and pointer-friendly.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlass, X, Check } from '@phosphor-icons/react';
import { Input } from './ui/input';

const RESULT_CAP = 12;

export default function SkuVariantPicker({
  skus,
  variants,
  value,
  onChange,
  mode = 'variant',
  testIdPrefix = 'sku-picker',
  placeholder = 'Search SKU code, name, or barcode…',
  autoFocus = false,
}) {
  const [term, setTerm] = useState('');
  const [expandedSkuId, setExpandedSkuId] = useState(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Group variants by SKU once per props change.
  const variantsBySku = useMemo(() => {
    const m = {};
    for (const v of variants || []) {
      if (!v?.sku_id) continue;
      (m[v.sku_id] = m[v.sku_id] || []).push(v);
    }
    return m;
  }, [variants]);

  const skusById = useMemo(
    () => Object.fromEntries((skus || []).map((s) => [s.id, s])),
    [skus]
  );

  const variantsById = useMemo(
    () => Object.fromEntries((variants || []).map((v) => [v.id, v])),
    [variants]
  );

  // What's currently selected — drives the "chosen chip" header.
  // In 'sku' mode `value` is a sku_id; in 'variant' mode (default) it's a variant_id.
  const selectedSku = value && mode === 'sku' ? skusById[value] : null;
  const selectedVariant = value && mode !== 'sku' ? variantsById[value] : null;
  const chipSku = selectedSku || (selectedVariant ? skusById[selectedVariant.sku_id] : null);

  // Search filter (top-level on SKUs, fall through to barcode on variants).
  const matchedSkus = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!t) return [];
    const matches = [];
    for (const s of skus || []) {
      const code = (s.sku_code || '').toLowerCase();
      const name = (s.name || '').toLowerCase();
      let hit = code.includes(t) || name.includes(t);
      if (!hit) {
        // Fall through to barcodes on this SKU's variants — receivers often
        // know the carton barcode but not the SKU code.
        const vs = variantsBySku[s.id] || [];
        hit = vs.some((v) => (v.barcode || '').toLowerCase().includes(t));
      }
      if (hit) matches.push(s);
      if (matches.length >= RESULT_CAP * 2) break; // tiny over-cap for ranking
    }
    // Ranking: exact code match first, then prefix code match, then everything else
    matches.sort((a, b) => {
      const ac = (a.sku_code || '').toLowerCase();
      const bc = (b.sku_code || '').toLowerCase();
      const aScore = ac === t ? 0 : ac.startsWith(t) ? 1 : 2;
      const bScore = bc === t ? 0 : bc.startsWith(t) ? 1 : 2;
      return aScore - bScore;
    });
    return matches.slice(0, RESULT_CAP);
  }, [term, skus, variantsBySku]);

  // Close the result panel when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setExpandedSkuId(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const variantSuffix = (v) =>
    [v.size, v.color, v.barcode].filter(Boolean).join(' · ');

  const pickVariant = (variantId) => {
    onChange(variantId);
    setTerm('');
    setExpandedSkuId(null);
    setOpen(false);
  };

  const onSkuClick = (sku) => {
    if (mode === 'sku') {
      // SKU-only mode — emit the sku_id directly, no variant chooser.
      onChange(sku.id);
      setTerm('');
      setExpandedSkuId(null);
      setOpen(false);
      return;
    }
    const vs = variantsBySku[sku.id] || [];
    if (vs.length === 1) {
      pickVariant(vs[0].id);
    } else if (vs.length === 0) {
      // No variants — silent no-op (shouldn't normally happen since orphan
      // SKUs auto-create a default variant elsewhere in the app).
      return;
    } else {
      setExpandedSkuId(expandedSkuId === sku.id ? null : sku.id);
    }
  };

  const clearSelection = () => {
    onChange('');
    setTerm('');
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  // -- Render --
  if ((selectedVariant || selectedSku) && !open) {
    const variantSummary = selectedVariant ? variantSuffix(selectedVariant) : '';
    return (
      <div className="flex items-center gap-2" data-testid={`${testIdPrefix}-chip`}>
        <div className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 border border-border rounded bg-muted/40">
          <Check size={14} weight="bold" className="text-emerald-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-mono text-sm font-bold truncate">
              {chipSku?.sku_code || selectedVariant?.barcode || selectedVariant?.id?.slice(-8) || selectedSku?.id?.slice(-8)}
            </div>
            <div className="text-[11px] text-muted-foreground truncate">
              {[chipSku?.name, variantSummary].filter(Boolean).join(' · ') || '—'}
            </div>
          </div>
          <button
            type="button"
            onClick={clearSelection}
            data-testid={`${testIdPrefix}-change`}
            className="text-xs font-bold uppercase tracking-wider text-brand hover:underline shrink-0"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <MagnifyingGlass
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        />
        <Input
          ref={inputRef}
          type="search"
          value={term}
          onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          data-testid={`${testIdPrefix}-input`}
          className="pl-9 h-9"
          autoComplete="off"
        />
        {term && (
          <button
            type="button"
            onClick={() => { setTerm(''); inputRef.current?.focus(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && term && (
        <div
          className="absolute left-0 right-0 z-30 mt-1 bg-card border border-border rounded-md shadow-md max-h-[280px] overflow-y-auto"
          data-testid={`${testIdPrefix}-results`}
        >
          {matchedSkus.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground text-center" data-testid={`${testIdPrefix}-empty`}>
              No SKUs match "{term}"
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {matchedSkus.map((sku) => {
                const vs = variantsBySku[sku.id] || [];
                const isExpanded = expandedSkuId === sku.id;
                return (
                  <li key={sku.id}>
                    <button
                      type="button"
                      onClick={() => onSkuClick(sku)}
                      data-testid={`${testIdPrefix}-result-${sku.sku_code}`}
                      className="w-full text-left px-3 py-2 hover:bg-muted flex items-center gap-2"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-sm font-bold truncate">{sku.sku_code}</div>
                        {sku.name && <div className="text-[11px] text-muted-foreground truncate">{sku.name}</div>}
                      </div>
                      <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                        vs.length === 0 ? 'bg-rose-50 text-rose-700' :
                        vs.length === 1 ? 'bg-emerald-50 text-emerald-700' :
                        'bg-blue-50 text-blue-700'
                      }`}>
                        {vs.length === 0 ? 'no variants'
                          : vs.length === 1 ? '1 variant'
                          : `${vs.length} variants${isExpanded ? ' ▾' : ' ▸'}`}
                      </span>
                    </button>
                    {isExpanded && vs.length > 1 && (
                      <ul className="bg-muted/40 border-t border-border" data-testid={`${testIdPrefix}-variants-${sku.sku_code}`}>
                        {vs.map((v) => (
                          <li key={v.id}>
                            <button
                              type="button"
                              onClick={() => pickVariant(v.id)}
                              data-testid={`${testIdPrefix}-variant-${v.id}`}
                              className="w-full text-left pl-8 pr-3 py-2 text-sm hover:bg-card flex items-center gap-2"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="font-mono text-xs truncate">
                                  {variantSuffix(v) || v.id.slice(-8)}
                                </div>
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
